import { getEnvVariables } from '../constants/getEnvVariables';
import * as Sentry from '@sentry/react';
import { getClientApp, getClientVersion } from '../lib/clienteWeb';
import {
  leerAccessToken,
  leerRefreshToken,
  guardarAccessToken,
  limpiarSesion,
  haySesion,
  rutDeSesion,
  clasificarRespuestaAuth,
  REFRESH_URL,
  REFRESH_AUTHORIZATION,
} from '../lib/sesionTokens';

const { API_URL } = getEnvVariables();

class ApiCore {

  // El token JWT puede expirar o invalidarse a mitad de sesión (el bootstrap de
  // token invitado en Routers.jsx solo corre una vez al montar). Sin esto, cualquier
  // endpoint autenticado queda 403 "Token inválido o expirado." de forma silenciosa
  // hasta que el usuario recarga la página manualmente.
  _isExpiredTokenError = (data) => {
    const message = typeof data === 'string' ? data : (data && (data.message || data.msg)) || '';
    return typeof message === 'string' && /token/i.test(message) && /(inv[aá]lido|expirado)/i.test(message);
  }

  // Headers de telemetría (X-Client-App / X-Client-Version), sin efecto
  // funcional: identifican qué app/versión origina cada request para medir
  // adopción, de cara a un control de seguridad futuro. Se agregan tanto en
  // los métodos autenticados (_headers) como en postNoAuth.
  _telemetryHeaders = () => ({
    'X-Client-App': getClientApp(),
    'X-Client-Version': getClientVersion(),
  })

  // Punto único de armado de headers para los métodos autenticados. Lee
  // token/rut de localStorage en cada llamada (nunca los cachea: el token
  // rota vía _refreshToken). `withContentType` preserva el hecho de que hoy
  // no todos los métodos mandan `Content-type` (los que solo leen, GET/DELETE
  // simples, no lo mandaban). `rutFallback` preserva la diferencia existente
  // entre métodos que usan `rut || '0'` (string) y los que usan `rut || 0`
  // (number) — no se uniforma, cada call site pasa el que ya tenía.
  // Con sesión de usuario (access token JWT real, ver src/lib/sesionTokens.js
  // y CLAUDE.md § Sesión JWT / token invitado), el access de sesión y el rut
  // derivado de su claim ganan sobre el token/rut del invitado. Sin sesión,
  // comportamiento idéntico al de siempre.
  _headers = ({ withContentType = true, rutFallback = '0' } = {}) => {
    // haySesion() exige access Y refresh no vacíos (caso borde 3): un access
    // huérfano (sin refresh, p. ej. por una escritura parcial en cuota llena)
    // no cuenta como sesión válida y debe degradar al token/rut invitado.
    const accessSesion = haySesion() ? leerAccessToken() : null;
    const token = accessSesion || localStorage.getItem('token');
    const rut = accessSesion ? rutDeSesion(accessSesion) : localStorage.getItem('rut');

    return {
      'Accept': 'application/json',
      ...(withContentType ? { 'Content-type': 'application/json' } : {}),
      'Authorization': `Bearer ${token}`,
      'rut': rut || rutFallback,
      ...this._telemetryHeaders(),
    };
  }

  // Token invitado (comportamiento histórico, CA-33): re-pide uno nuevo vía
  // generaToken. Nunca se usa para renovar una sesión de usuario real (CA-3/CA-9).
  _refreshToken = async () => {
    // Usar postNoAuth que ya maneja la lógica de generación y guardado del token
    const respApi = await this.postNoAuth(`/apiV2/usuario/generaToken`, {});

    if (!respApi.token) {
      throw new Error('No se pudo renovar el token');
    }

    return respApi.token;
  }

  // Único punto que renueva el access token de sesión (CA-8/CA-9/CA-11/CA-13).
  // Single-flight: N llamadas concurrentes comparten la misma promesa, así que
  // el fetch a refreshToken se dispara una sola vez (CA-11) y ninguna de ellas
  // participa a su vez del interceptor de renovación (CA-12b).
  _refreshSesion = async () => {
    if (this._refreshEnVuelo) return this._refreshEnVuelo;

    this._refreshEnVuelo = (async () => {
      const refreshToken = leerRefreshToken();
      if (!refreshToken) {
        this._forzarLogoutSesion();
        return false;
      }

      try {
        // CA-34: qué Authorization lleva esta llamada es una decisión explícita
        // (REFRESH_AUTHORIZATION en sesionTokens.js), no un accidente — por
        // defecto el token invitado, nunca el access de sesión que en este
        // escenario está justamente vencido/rechazado.
        const authToken = REFRESH_AUTHORIZATION === 'invitado'
          ? localStorage.getItem('token')
          : leerAccessToken();

        const response = await fetch(`${API_URL}${REFRESH_URL}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
            ...this._telemetryHeaders(),
          },
          body: JSON.stringify({ refreshToken }),
        });

        let body = null;
        try {
          body = await response.json();
        } catch {
          body = null;
        }

        const accessTokenNuevo = body && typeof body.accessToken === 'string' ? body.accessToken : '';

        if (!response.ok || !body || body.status === 'error' || !accessTokenNuevo) {
          this._forzarLogoutSesion(body?.statusMessage, response.status);
          return false;
        }

        // CA-13: solo se guarda el access — el refresh nunca se rota ni se toca.
        guardarAccessToken(accessTokenNuevo);
        this._sesionExpiradaEmitida = false;
        return true;
      } catch (error) {
        Sentry.captureException(error, { extra: { method: 'POST', url: REFRESH_URL } });
        this._forzarLogoutSesion();
        return false;
      }
    })();

    try {
      return await this._refreshEnVuelo;
    } finally {
      this._refreshEnVuelo = null;
    }
  }

  // Wrapper público (CA-19/CA-36): ModalCredential/ProfileForm lo usan para
  // reponer una sesión tras completar/cambiar el rut, sin acceder a estado interno.
  refrescarSesion = async () => this._refreshSesion();

  // Logout forzado por un refresh de sesión fallido (CA-7/CA-10/CA-38). Limpia
  // ambas claves de sesión y el `rut` del invitado (nunca deja claim/header
  // divergentes, CA-6), reporta a Sentry SIN financial_risk (no hay cargo
  // involucrado) y avisa una sola vez por fallo vía CustomEvent — lo escucha
  // src/hooks/useSesionExpirada.js, montado una vez en Routers.jsx.
  _forzarLogoutSesion = (statusMessage, httpStatus) => {
    limpiarSesion();
    try {
      localStorage.removeItem('rut');
    } catch {
      // no-op: modo privado / storage lleno.
    }

    Sentry.captureMessage('sesion_refresh_fallido', {
      level: 'error',
      tags: { flujo: 'auth' },
      extra: { httpStatus, statusMessage },
    });

    if (!this._sesionExpiradaEmitida) {
      this._sesionExpiradaEmitida = true;
      try {
        window.dispatchEvent(new CustomEvent('sima:sesion-expirada'));
      } catch {
        // entorno sin `window` (tests puros de nodo): no-op.
      }
    }
  }

  // Árbitro único de renovación (CA-8/CA-9/CA-11/CA-14/CA-33): decide, para una
  // respuesta que "huele a" token rechazado, si (a) no hay nada que renovar
  // (statusMessage no-refrescable o desconocido), (b) hay que refrescar la
  // sesión real (nunca degrada a token invitado, CA-9), o (c) —sin sesión
  // activa— re-pedir el token invitado, igual que el comportamiento de hoy.
  _renovarCredencial = async (body) => {
    const clase = clasificarRespuestaAuth(body);
    if (clase === 'no-refrescable' || clase === 'otro') return false;

    if (haySesion()) {
      return this._refreshSesion();
    }

    await this._refreshToken();
    return true;
  }

  get = async (url, _retried = false) => {
    try {
      const response = await fetch(`${API_URL}${url}`, {
        method: 'GET',
        headers: this._headers({ withContentType: false, rutFallback: '0' }),
      })

      // console.log('GET - Respuesta de la API (objeto Response):', response);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          errorData = jsonError;
        }

        if (response.status === 403 && !_retried && await this._renovarCredencial(errorData)) {
          return this.get(url, true);
        }

        console.error('GET - Error en la respuesta de la API (HTTP no exitoso):', response.status, response.statusText, errorData);
        throw new Error(errorData.msg || `Error de red: ${response.status} ${response.statusText}`);
      }

      const respApiData = await response.json();
      // console.log('GET - Datos de la API (JSON parseado):', respApiData);

      // Retorna la data útil. Ajusta `respApiData.data` si tu API anida la respuesta.
      return respApiData.data || respApiData;

    } catch (catchError) {
      console.error('GET - Error general en la llamada GET:', catchError);
      Sentry.captureException(catchError, { extra: { method: 'GET', url } });
      throw catchError;
    }
  }

  post = async (url, data, _retried = false) => {
    try {
      const respApi = await fetch(`${API_URL}${url}`, {
        method: 'POST',
        headers: this._headers({ rutFallback: 0 }),
        body: JSON.stringify(data)
      }).then(resp => resp.json());
      // console.log('POST- Datos de la API (JSON parseado):', respApi);

      if (!_retried && await this._renovarCredencial(respApi)) {
        return this.post(url, data, true);
      }

      return respApi.data || respApi.datos;
    } catch (catchError) {
      Sentry.captureException(catchError, { extra: { method: 'POST', url } });
      throw catchError;
    }
  }

  // options.signal: AbortSignal opcional (p. ej. desde un AbortController con
  // timeout, como en MercadoPagoReturnPage.jsx). Aditivo: los llamadores que
  // no lo pasan siguen funcionando igual (options = {} por defecto).
  post2 = async (url, data, options = {}, _retried = false) => {
    try {
      const respApi = await fetch(`${API_URL}${url}`, {
        method: 'POST',
        headers: this._headers({ rutFallback: 0 }),
        body: JSON.stringify(data),
        signal: options.signal
      }).then(resp => resp.json());

      if (!_retried && await this._renovarCredencial(respApi)) {
        return this.post2(url, data, options, true);
      }

      return respApi;
    } catch (catchError) {
      // El AbortError de nuestro propio timeout de cliente no es un fallo de
      // la API: se reporta una sola vez, con contexto, desde la página que
      // decidió abortar (evita ruido duplicado en Sentry).
      if (catchError?.name !== 'AbortError') {
        Sentry.captureException(catchError, { extra: { method: 'POST', url } });
      }
      throw catchError;
    }
  }

  // postFull: espejo de getFull (valida response.ok y propaga el error, en
  // vez de tragárselo como hace `post`). Método nuevo y aditivo: no reemplaza
  // a `post`, que sirve a los flujos de pago y no debe volverse estricto
  // (varios endpoints de pago devuelven errores de negocio con HTTP 200).
  // Usarlo solo en endpoints donde SÍ se necesita que el catch de la UI se
  // dispare de verdad (p. ej. sendOrderEmail).
  postFull = async (url, data, _retried = false) => {
    try {
      const response = await fetch(`${API_URL}${url}`, {
        method: 'POST',
        headers: this._headers({ rutFallback: 0 }),
        body: JSON.stringify(data)
      });

      let body;
      try {
        body = await response.json();
      } catch (jsonError) {
        // Proxy devolvió HTML no-JSON (502/504) u otra respuesta no parseable.
        const parseError = new Error('La respuesta del servidor no es válida.');
        parseError.httpStatus = response.status;
        throw parseError;
      }

      if (!response.ok) {
        if (response.status === 403 && !_retried && await this._renovarCredencial(body)) {
          return this.postFull(url, data, true);
        }

        const error = new Error(body?.message || body?.msg || `Error de red: ${response.status} ${response.statusText}`);
        error.httpStatus = response.status;
        error.backendMessage = body?.message || body?.msg;
        error.backendCode = body?.statusMessage || body?.code;
        throw error;
      }

      // Contrato "soft error" con HTTP 2xx (precedente: MercadoPagoReturnPage.jsx / Api.post2).
      if (body && body.status === 'error') {
        const error = new Error(body.message || 'La operación no se completó.');
        error.httpStatus = response.status;
        error.backendMessage = body.message;
        error.backendCode = body.statusMessage || body.code;
        throw error;
      }

      return body;
    } catch (catchError) {
      Sentry.captureException(catchError, { extra: { method: 'POST', url } });
      throw catchError;
    }
  }

  put = async (url, data, _retried = false) => { // Asegúrate de que `put` esté definido como una propiedad del objeto `Api`
    try {
      // 1. Await directamente la llamada fetch para obtener el objeto Response.
      const response = await fetch(`${API_URL}${url}`, {
        method: 'PUT',
        headers: this._headers({ rutFallback: '0' }),
        body: JSON.stringify(data)
      });

      // 2. Imprime la respuesta completa de la red (Response object)
      //console.log('Respuesta de la API (objeto Response):', response);

      // 3. Verifica si la respuesta HTTP fue exitosa (código 2xx)
      // `response.ok` es un booleano que indica si la respuesta HTTP es exitosa (estado 200-299)
      if (!response.ok) {
        let errorData;
        try {
          // Intenta parsear el cuerpo del error como JSON
          errorData = await response.json();
        } catch (jsonError) {
          // Si no es JSON, usa el texto de la respuesta o un mensaje genérico
          errorData = await response.text();
        }

        if (response.status === 403 && !_retried && await this._renovarCredencial(errorData)) {
          return this.put(url, data, true);
        }

        console.error('Error en la respuesta de la API (HTTP no exitoso):', response.status, response.statusText, errorData);
        // Lanza un error con el mensaje del backend o un mensaje predeterminado
        throw new Error(errorData.msg || `Error de red: ${response.status} ${response.statusText}`);
      }

      // 4. Parsea el cuerpo de la respuesta como JSON
      const respApiData = await response.json();

      // 5. Imprime la data parseada del cuerpo de la respuesta (el JSON de tu API)
      //console.log('Datos de la API (JSON parseado):', respApiData);

      // 6. Ahora puedes verificar `respApiData.code` si tu backend lo devuelve
      // Es más común que la API devuelva un objeto con la data en caso de éxito
      // y un objeto con detalles del error en caso de fallo, y que el estado HTTP
      // (response.ok, response.status) ya te indique si la operación fue exitosa o no.
      // Si tu API insiste en un `code` dentro del JSON incluso en éxito, podrías hacer:
      if (respApiData.code && respApiData.code !== 200) {
        throw new Error(respApiData.msg || 'Error en la respuesta de la API.');
      }

      // Retorna la data útil de la respuesta
      return respApiData.data || respApiData; // Asumiendo que la data útil está en `data` o es el objeto completo
    } catch (catchError) {
      console.error('Error general en la llamada PUT:', catchError);
      Sentry.captureException(catchError, { extra: { method: 'PUT', url } });
      // Relanza el error para que `useMutation` pueda manejarlo con `onError`
      throw catchError;
    }
  }


  delete = async (url, _retried = false) => {
    try {
      const response = await fetch(`${API_URL}${url}`, {
        method: 'DELETE',
        headers: this._headers({ withContentType: false, rutFallback: '0' }),
      });

      //le.log('DELETE - Respuesta de la API (objeto Response):', response);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          errorData = await response.text();
        }

        if (response.status === 403 && !_retried && await this._renovarCredencial(errorData)) {
          return this.delete(url, true);
        }

        console.error('DELETE - Error en la respuesta de la API (HTTP no exitoso):', response.status, response.statusText, errorData);
        // ¡Importante! Lanza un Error con un mensaje.
        throw new Error(errorData.msg || `Error de red DELETE: ${response.status} ${response.statusText}`);
      }

      const respApiData = await response.json();
      //console.log('DELETE - Datos de la API (JSON parseado):', respApiData);

      return respApiData.data || respApiData;

    } catch (catchError) {
      console.error('DELETE - Error general en la llamada DELETE:', catchError);
      Sentry.captureException(catchError, { extra: { method: 'DELETE', url } });
      throw catchError; // Relanza el error
    }
  }

  delete2 = async (url, data = {}, _retried = false) => {
    try {
      const respApi = await fetch(`${API_URL}${url}`, {
        method: 'DELETE',
        headers: this._headers({ rutFallback: 0 }),
        body: JSON.stringify(data)
      }).then(resp => resp.json());

      if (!_retried && await this._renovarCredencial(respApi)) {
        return this.delete2(url, data, true);
      }

      return respApi;
    } catch (catchError) {
      Sentry.captureException(catchError, { extra: { method: 'DELETE', url } });
      throw catchError;
    }
  }

  delete3 = async (url, body, _retried = false) => { // Ahora recibe 'body' como argumento
    try {
      // Construye la URL dinámicamente usando los datos del 'body'
      // NOTA: Asegúrate de que `api.deleteTBK` termine con un `/` si esperas el formato de tu ejemplo.
      // O ajusta si no lo tiene. Por ejemplo: /oneClick/deleteInscription/
      const urlToDelete = `${API_URL}${url}/${body.usuario_id}/${body.codigo_comercio}/${body.tbk_card}`;

      const response = await fetch(urlToDelete, {
        method: 'DELETE',
        headers: this._headers({ withContentType: false, rutFallback: '0' }),
      });

      // console.log('DELETE - Respuesta de la API (objeto Response):', response);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          errorData = await response.text();
        }

        if (response.status === 403 && !_retried && await this._renovarCredencial(errorData)) {
          return this.delete3(url, body, true);
        }

        console.error('DELETE - Error en la respuesta de la API (HTTP no exitoso):', response.status, response.statusText, errorData);
        throw new Error(errorData.msg || `Error de red DELETE: ${response.status} ${response.statusText}`);
      }

      const respApiData = await response.json();
      // console.log('DELETE - Datos de la API (JSON parseado):', respApiData);

      return respApiData.data || respApiData;

    } catch (catchError) {
      console.error('DELETE - Error general en la llamada DELETE:', catchError);
      // urlToDelete es const dentro del try, no visible acá — usamos el template original
      Sentry.captureException(catchError, { extra: { method: 'DELETE', url } });
      throw catchError;
    }
  }

  getFull = async (url, _retried = false) => {
    try {
      const response = await fetch(`${API_URL}${url}`, {
        method: 'GET',
        headers: this._headers({ withContentType: false, rutFallback: '0' }),
      });

      if (!response.ok) {
        if (response.status === 403 && !_retried) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (jsonError) {
            errorData = null;
          }
          if (await this._renovarCredencial(errorData)) {
            return this.getFull(url, true);
          }
        }
        throw new Error(`Error de red: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (catchError) {
      Sentry.captureException(catchError, { extra: { method: 'GET', url } });
      throw catchError;
    }
  }

  postNoAuth = async (url, data) => {
    try {
      const respApi = await fetch(`${API_URL}${url}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-type': 'application/json',
          ...this._telemetryHeaders(),
        },
        body: JSON.stringify(data)
      }).then(resp => resp.json());

      if (!respApi.token) {
        throw "Error al obtener el token";
      }

      localStorage.setItem('token', respApi.token);

      return respApi;
    } catch (catchError) {
      console.log("Error", catchError);
      Sentry.captureException(catchError, { extra: { method: 'POST', url } });
      throw catchError;
    }
  }

  postPin = async (url, data, _retried = false) => {
    try {
      const respApi = await fetch(`${API_URL}${url}`, {
        method: 'POST',
        headers: this._headers({ rutFallback: 0 }),
        body: JSON.stringify(data)
      }).then(resp => resp.json());

      if (!_retried && await this._renovarCredencial(respApi)) {
        return this.postPin(url, data, true);
      }

      return respApi;
    } catch (catchError) {
      console.log("Error", catchError);
      Sentry.captureException(catchError, { extra: { method: 'POST', url } });
      throw catchError;
    }
  }

  // postLogin: usado exclusivamente por los 3 emisores de sesión (loginUsuario/
  // loginGoogle/loginApple, CA-15/CA-17/CA-18). Siempre manda el token invitado
  // (nunca un access de sesión previo — loguearse no debe viajar con la sesión
  // de otro usuario), nunca escribe en localStorage, y no participa del
  // interceptor de renovación (un login que falla no debe disparar un refresh).
  postLogin = async (url, data) => {
    try {
      const respApi = await fetch(`${API_URL}${url}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...this._telemetryHeaders(),
        },
        body: JSON.stringify(data)
      }).then(resp => resp.json());

      return respApi;
    } catch (catchError) {
      Sentry.captureException(catchError, { extra: { method: 'POST', url } });
      throw catchError;
    }
  }
}



export { ApiCore }